import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus, Search, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { useAuth } from '../auth/KeycloakContext';

interface MumbleServer {
  id: string;
  name: string;
  ipv4: string | null;
  host: string;
  port: number;
  ice_host: string | null;
  ice_port: number | null;
  ice_secret: string | null;
  max_users: number;
  max_bandwidth: number;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  tenant_count: number;
}

export default function MumbleServers() {
  const navigate = useNavigate();
  const { keycloak } = useAuth();
  const [servers, setServers] = useState<MumbleServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (keycloak?.token) {
      loadData();
    }
  }, [keycloak?.token]);

  const loadData = async () => {
    setLoading(true);
    const res = await fetch('/api/mumble-servers', {
      headers: { 'Authorization': `Bearer ${keycloak?.token}` }
    });
    setServers(await res.json());
    setLoading(false);
  };

  // Filter servers by search
  const filteredServers = servers.filter((server) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      server.name.toLowerCase().includes(searchLower) ||
      server.host.toLowerCase().includes(searchLower)
    );
  });

  // Pagination calculations
  const totalServers = filteredServers.length;
  const totalPages = Math.ceil(totalServers / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentServers = filteredServers.slice(startIndex, endIndex);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(currentServers.map(s => s.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectServer = (serverId: string, checked: boolean) => {
    const newSet = new Set(selectedIds);
    if (checked) {
      newSet.add(serverId);
    } else {
      newSet.delete(serverId);
    }
    setSelectedIds(newSet);
  };

  const handleDeleteServers = async () => {
    if (selectedIds.size === 0) return;

    // Check if any selected server has tenants
    const serversWithTenants = servers.filter(s => selectedIds.has(s.id) && s.tenant_count > 0);
    if (serversWithTenants.length > 0) {
      alert(`Folgende Server können nicht gelöscht werden, da sie noch Einrichtungen haben: ${serversWithTenants.map(s => s.name).join(', ')}`);
      return;
    }

    if (!confirm(`Möchten Sie ${selectedIds.size} Server wirklich löschen?`)) return;

    setDeleting(true);
    for (const id of Array.from(selectedIds)) {
      await fetch(`/api/mumble-servers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${keycloak?.token}` }
      });
    }
    setSelectedIds(new Set());
    await loadData();
    setDeleting(false);
  };

  const allSelected = currentServers.length > 0 && currentServers.every(s => selectedIds.has(s.id));

  const handleSetDefault = async (server: MumbleServer) => {
    await fetch(`/api/mumble-servers/${server.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak?.token}`
      },
      body: JSON.stringify({ is_default: true }),
    });
    loadData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-sayses-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">Mumble Server</h1>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-200 flex items-center gap-4">
          <button
            onClick={() => navigate('/mumble-servers/new')}
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="Server hinzufügen"
          >
            <Plus size={20} />
          </button>
          <button
            onClick={handleDeleteServers}
            disabled={selectedIds.size === 0 || deleting}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Ausgewählte Server löschen"
          >
            <Minus size={20} />
          </button>
          <div className="flex-1 flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Suchen..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
              />
            </div>
            <span className="text-sm text-slate-600">Alle auswählen</span>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
            />
          </div>
        </div>

        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="w-12 px-6 py-3"></th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Name</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Host</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">ICE</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Einrichtungen</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {currentServers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-slate-500">
                  Keine Mumble Server vorhanden.
                </td>
              </tr>
            ) : (
              currentServers.map((server) => (
                <tr key={server.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(server.id)}
                      onChange={(e) => handleSelectServer(server.id, e.target.checked)}
                      className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/mumble-servers/${server.id}/edit`)}
                        className="text-sayses-accent hover:text-sayses-accent-hover underline"
                      >
                        {server.name}
                      </button>
                      {server.is_default && (
                        <span title="Standard-Server">
                          <Star size={14} className="text-yellow-500 fill-yellow-500" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {server.host}:{server.port}
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {server.ice_host ? `${server.ice_host}:${server.ice_port}` : '-'}
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {server.tenant_count}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          server.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {server.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                      {!server.is_default && (
                        <button
                          onClick={() => handleSetDefault(server)}
                          className="text-xs text-slate-400 hover:text-yellow-600 transition-colors"
                          title="Als Standard setzen"
                        >
                          <Star size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalServers > 0 && (
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Einträge pro Seite:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="border border-slate-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                {startIndex + 1}-{Math.min(endIndex, totalServers)} von {totalServers}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={20} className="text-slate-600" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={20} className="text-slate-600" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
